import type { ICustomFieldDefinition, IFieldOverride, IFormGroup, IFormPatch, IRecordTypeOverride } from '@platform/core/types';

type Change<T> = { [K in keyof T]?: T[K] | undefined };

/** Без порожнього значення ключа: порожній об'єкт чи масив — вилучити ключ патча. */
function setOrDrop<K extends keyof IRecordTypeOverride>(patch: IRecordTypeOverride, key: K, value: IRecordTypeOverride[K]): IRecordTypeOverride {
  const out = { ...patch };
  const empty = !value || (Array.isArray(value) ? !value.length : !Object.keys(value as object).length);
  if (empty) delete out[key];
  else out[key] = value;
  return out;
}

/**
 * Зміна перевизначення поля (ADR-023 `fields.<ключ>`): задані ключі замінюють наявні, undefined — вилучає (повернення до опублікованого);
 * порожнє перевизначення поля зникає з патча. Чиста функція.
 */
export function withFieldOverride(patch: IRecordTypeOverride, key: string, change: Change<IFieldOverride>): IRecordTypeOverride {
  const current: Record<string, unknown> = { ...(patch.fields?.[key] ?? {}) };
  for (const [k, v] of Object.entries(change)) {
    if (v === undefined) delete current[k];
    else current[k] = v;
  }
  const fields = { ...(patch.fields ?? {}) };
  if (Object.keys(current).length) fields[key] = current as IFieldOverride;
  else delete fields[key];
  return setOrDrop(patch, 'fields', fields);
}

/** Зміна власного поля простору (`customFields[]` за ключем): задані ключі замінюють, undefined — вилучає. Чиста функція. */
export function withCustomField(patch: IRecordTypeOverride, key: string, change: Change<ICustomFieldDefinition>): IRecordTypeOverride {
  const list = (patch.customFields ?? []).map((f) => {
    if (f.key !== key) return f;
    const next: Record<string, unknown> = { ...f };
    for (const [k, v] of Object.entries(change)) {
      if (v === undefined) delete next[k];
      else next[k] = v;
    }
    return next as unknown as ICustomFieldDefinition;
  });
  return setOrDrop(patch, 'customFields', list);
}

/** Нове власне поле наприкінці переліку (сервер ставить його перед тегами й у форму за типом). Чиста функція. */
export function addCustomField(patch: IRecordTypeOverride, field: ICustomFieldDefinition): IRecordTypeOverride {
  return { ...patch, customFields: [...(patch.customFields ?? []), field] };
}

/** Вилучити власне поле: з переліку, з його кольорів і з явних посилань патча на нього (колонки, поля подань, форми). Чиста функція. */
export function removeCustomField(patch: IRecordTypeOverride, key: string): IRecordTypeOverride {
  const out = setOrDrop(withFieldOverride(patch, key, { colors: undefined }), 'customFields', (patch.customFields ?? []).filter((f) => f.key !== key));
  return stripKey(out, key);
}

const without = (list: unknown, key: string) => (Array.isArray(list) ? list.filter((k) => k !== key) : list);

/** Явні посилання патча на поле (views.columns, settings.*.columns|fields|dateFields, forms.*.keyLine|underTitle|groups|fields) — без нього. */
export function stripKey(patch: IRecordTypeOverride, key: string): IRecordTypeOverride {
  const json = structuredClone(patch) as IRecordTypeOverride;
  if (json.views) {
    json.views.columns = without(json.views.columns, key) as string[] | null | undefined;
    for (const s of Object.values(json.views.settings ?? {})) {
      if (!s) continue;
      for (const k of ['columns', 'fields', 'dateFields'] as const) if (s[k]) s[k] = without(s[k], key) as string[];
    }
  }
  for (const form of Object.values(json.forms ?? {}) as Array<IFormPatch | null | undefined>) {
    if (!form) continue;
    for (const k of ['keyLine', 'underTitle', 'fields'] as const) if (form[k]) form[k] = without(form[k], key) as string[];
    if (form.groups) form.groups = form.groups.map((g: IFormGroup) => ({ ...g, fields: g.fields.filter((f) => f !== key), layout: g.layout?.filter((l) => l.key !== key) }));
  }
  return json;
}
